import { format, subDays } from "date-fns";
import type { Room } from "@/types/database";

export interface AvailabilitySnapshot {
  id: string;
  room_id: string;
  studio_id: string;
  snapshot_date: string;
  total_operating_slots: number;
  available_instant_slots: number;
  available_approval_slots: number;
  unavailable_slots: number;
  booked_slots: number;
  vacancy_rate: number;
  occupancy_rate: number;
  instant_availability_rate: number;
  daily_revenue?: number;
  average_booking_value?: number;
  cancellation_rate?: number;
  cancellations?: number;
  total_bookings?: number;
  created_at?: string;
  snapshot_taken_at: string;
}

export const generateMockData = (days: number, rooms: Room[]) => {
  const mockSnapshots: AvailabilitySnapshot[] = [];

  // Create mock rooms if none exist
  const mockRooms =
    rooms.length > 0
      ? rooms
      : [
          {
            id: "mock-room-1",
            name: "Studio A",
            studio_id: "mock-studio",
          } as Room,
          {
            id: "mock-room-2",
            name: "Studio B",
            studio_id: "mock-studio",
          } as Room,
          {
            id: "mock-room-3",
            name: "Dance Floor 1",
            studio_id: "mock-studio",
          } as Room,
          {
            id: "mock-room-4",
            name: "Rehearsal Room",
            studio_id: "mock-studio",
          } as Room,
        ];

  for (let i = 0; i < days; i++) {
    const currentDate = subDays(new Date(), days - i - 1);
    const progress = i / days; // Progress from 0 to 1 over time

    mockRooms.forEach((room, roomIndex) => {
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      // Room characteristics
      const roomPopularity = [0.90, 0.85, 0.80, 0.75][roomIndex % 4];
      const roomHourlyRate = [75, 60, 50, 40][roomIndex % 4];

      // Target average occupancy around 75% with upward trend (from 70% to 80%)
      const baseOccupancy = isWeekend
        ? roomPopularity * (0.75 + progress * 0.1) // Weekend: 75% -> 85%
        : roomPopularity * (0.65 + progress * 0.1); // Weekday: 65% -> 75%

      // Add realistic daily variation
      const dailyVariation = Math.sin(i * 0.5) * 0.05 + (Math.random() - 0.5) * 0.08;
      const occupancyRate = Math.max(0.5, Math.min(0.95, baseOccupancy + dailyVariation));

      const totalSlots = 48; // 30-minute slots for 24 hours
      const bookedSlots = Math.floor(totalSlots * occupancyRate);
      const availableSlots = totalSlots - bookedSlots;

      // Upward trend in revenue (increasing prices and bookings)
      const pricingMultiplier = 1 + progress * 0.3; // Prices increase 30% over time
      const hoursBooked = bookedSlots * 0.5;
      const dailyRevenue =
        hoursBooked * roomHourlyRate * pricingMultiplier * (isWeekend ? 1.2 : 1.0);
      
      const averageBookingValue =
        bookedSlots > 0 ? dailyRevenue / (bookedSlots / 4) : 0;

      // Downward trend in cancellation rate (from 12% to 3%)
      const baseCancellationRate = 12 - progress * 9;
      const cancellationRate = Math.max(
        1,
        baseCancellationRate + (Math.random() - 0.5) * 3
      );
      
      // Calculate actual number of bookings and cancellations
      // Assume average booking is 2 hours (4 slots), but ensure minimum bookings
      const bookingsPerDay = Math.max(3, Math.floor(bookedSlots / 4)); // At least 3 bookings per day
      const totalBookings = bookingsPerDay + Math.floor(Math.random() * 5); // Add some variation
      // Ensure we have some cancellations, especially early in the period
      const cancellations = Math.max(
        progress < 0.3 ? 1 : 0, // At least 1 cancellation in first 30% of period
        Math.round(totalBookings * cancellationRate / 100)
      );

      // Distribute available slots
      const instantRate = 0.3 + Math.random() * 0.4;
      const instantSlots = Math.floor(availableSlots * instantRate);
      const approvalSlots = Math.floor(availableSlots * (1 - instantRate) * 0.8);
      const unavailableSlots = availableSlots - instantSlots - approvalSlots;

      // Vacancy rate is inverse of occupancy (targeting ~25% average)
      const vacancyRate = ((totalSlots - bookedSlots) / totalSlots) * 100;

      mockSnapshots.push({
        id: `mock-${i}-${room.id}`,
        room_id: room.id,
        studio_id: room.studio_id || "mock-studio",
        snapshot_date: format(currentDate, "yyyy-MM-dd"),
        total_operating_slots: totalSlots,
        available_instant_slots: instantSlots,
        available_approval_slots: approvalSlots,
        unavailable_slots: unavailableSlots,
        booked_slots: bookedSlots,
        vacancy_rate: vacancyRate,
        occupancy_rate: occupancyRate * 100,
        instant_availability_rate: (instantSlots / totalSlots) * 100,
        daily_revenue: Math.round(dailyRevenue * 100) / 100,
        average_booking_value: Math.round(averageBookingValue * 100) / 100,
        cancellation_rate: Math.round(cancellationRate * 100) / 100,
        cancellations: cancellations,
        total_bookings: totalBookings,
        snapshot_taken_at: currentDate.toISOString(),
      });
    });
  }

  return { snapshots: mockSnapshots, rooms: mockRooms };
};